/**
 * The other orientations' rows, drawn into one plane's 3D scene for context.
 *
 * A plant on several orientations has one layout per plane, and each plane's
 * scene computes its own shading. What a designer still wants to see is where
 * the OTHER planes' tables stand - a west-facing field beside the south one,
 * the second roof face behind the first - so the picture is the plant and not
 * one field alone. Those tables are drawn as they were fitted, at their own
 * tilt and azimuth, and take no part in the scene's shading arithmetic: they
 * are a picture of the neighbours, not a second simulation.
 *
 * Every layout's tables carry their ends in longitude and latitude, and that
 * is what ties the planes together: the scene's own frame is metres east and
 * north of its tables' centroid, so another plane's tables are placed by
 * their offset from that centroid on the ground. A scene laid out from a
 * module count rather than a boundary has no centroid on the map, and then
 * nothing can be placed - the neighbours are simply not drawn.
 */
import { DEG, v3, add, mul, compass } from "./vec3";

const EARTH_R = 6371000;

/** Metres east and north of `origin` ([lon, lat]) for a [lon, lat] point. */
export function localXY(lonlat, origin) {
  if (!Array.isArray(lonlat) || !Array.isArray(origin)) return null;
  const [lon, lat] = lonlat.map(Number);
  const [lon0, lat0] = origin.map(Number);
  if (![lon, lat, lon0, lat0].every(Number.isFinite)) return null;
  return {
    x: EARTH_R * Math.cos(lat0 * DEG) * (lon - lon0) * DEG,
    y: EARTH_R * (lat - lat0) * DEG,
  };
}

/** The [lon, lat] centroid of a table list's ends, or null without one. */
export function tablesOrigin(tables) {
  const pts = (Array.isArray(tables) ? tables : [])
    .flatMap((t) => [t?.start_lonlat, t?.end_lonlat])
    .filter((p) => Array.isArray(p) && p.length >= 2 && p.every((v) => Number.isFinite(Number(v))));
  if (!pts.length) return null;
  return [
    pts.reduce((a, p) => a + Number(p[0]), 0) / pts.length,
    pts.reduce((a, p) => a + Number(p[1]), 0) / pts.length,
  ];
}

/**
 * Row quads for the other planes, in the scene's frame.
 *
 * @param planes [{ label, tilt, azimuth, tiers, clearance, moduleAlongSlope,
 *                  tables: [{ start_lonlat, end_lonlat, columns }] }]
 * @param origin the scene's tables' [lon, lat] centroid (tablesOrigin)
 * @param groundZ (x, y) => z, the scene's terrain
 * @returns [{ label, low: [p0, p1], high: [p0, p1], modules }]
 */
export function buildContextRows(planes, origin, groundZ = () => 0) {
  if (!origin || !Array.isArray(planes)) return [];
  const out = [];
  planes.forEach((plane) => {
    // Absence is not zero: an unset tilt is a plane that cannot be drawn,
    // not a flat one.
    const tilt = plane?.tilt == null || plane.tilt === "" ? NaN : Number(plane.tilt);
    const azimuth = Number(plane?.azimuth ?? 180);
    const tiers = Math.max(1, Math.trunc(Number(plane?.tiers) || 1));
    const alongSlope = Number(plane?.moduleAlongSlope);
    const clearance = Number.isFinite(Number(plane?.clearance)) ? Number(plane.clearance) : 0.5;
    if (!Number.isFinite(tilt) || !(alongSlope > 0)) return;
    const collector = alongSlope * tiers;
    const c = compass(azimuth);                     // the side the modules face
    const halfSpan = (collector * Math.cos(tilt * DEG)) / 2;
    const lowLift = clearance;
    const highLift = clearance + collector * Math.sin(tilt * DEG);
    (Array.isArray(plane.tables) ? plane.tables : []).forEach((t) => {
      const a = localXY(t?.start_lonlat, origin);
      const b = localXY(t?.end_lonlat, origin);
      if (!a || !b) return;
      const edge = (p, offset, lift) => {
        const q = add(v3(p.x, p.y, 0), mul(c, offset));
        return v3(q.x, q.y, groundZ(p.x, p.y) + lift);
      };
      out.push({
        label: plane.label || null,
        modules: Number(t?.columns) || null,
        low: [edge(a, halfSpan, lowLift), edge(b, halfSpan, lowLift)],
        high: [edge(a, -halfSpan, highLift), edge(b, -halfSpan, highLift)],
      });
    });
  });
  return out;
}
